/**
 * insightsService.ts — VS Insights Data Layer
 * =============================================
 * Fetches aggregate insights data from the server-side API endpoint
 * (GET /lender/insights) which runs with service_role and bypasses RLS.
 *
 * Tenant resolution (org_id → tenant_id) happens server-side.
 * This module handles API calls + transformation helpers.
 *
 * NO mock data. NO placeholder values. NO direct Supabase calls.
 */

import type {
  MetricValue,
  DataSourceStatus,
  Directionality,
  DataSufficiency,
  ScoreBandBucket,
} from '../types/insightsTypes';
import { SUFFICIENCY_THRESHOLDS, NOT_CONNECTED_REGISTRY } from '../types/insightsTypes';

// ============================================================================
// API configuration — matches lenderApiService.ts pattern
// ============================================================================
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;
const PURPOSE_HEADER = 'x-request-purpose';

// ============================================================================
// Raw DB row shapes (match view columns exactly)
// ============================================================================

export interface FinancialHealthRow {
  tenant_id: string;
  metric_date: string;
  score_band: string;
  member_count: number;
  avg_score: number | null;
  avg_dti: number | null;
  avg_reserves: number | null;
  avg_cash_flow_stability: number | null;
  monthly_avg_score: number | null;
}

export interface OverviewRow {
  tenant_id: string;
  metric_date: string;
  current_value: number | null;
  metric_key: string;
  monthly_value: number | null;
}

export interface AcquisitionRow {
  tenant_id: string;
  metric_month: string;
  metric_key: string;
  metric_value: number | null;
  metric_json: Record<string, unknown> | null;
}

// ============================================================================
// Transformer helpers (pure functions)
// ============================================================================

/** Classify sample size into sufficiency tier */
export function classifySufficiency(sampleSize: number): DataSufficiency {
  if (sampleSize <= SUFFICIENCY_THRESHOLDS.EMPTY) return 'empty';
  if (sampleSize <= SUFFICIENCY_THRESHOLDS.INSUFFICIENT_MAX) return 'insufficient';
  if (sampleSize <= SUFFICIENCY_THRESHOLDS.DIRECTIONAL_MAX) return 'directional';
  return 'standard';
}

/** Wrap a raw value in a MetricValue envelope */
export function toMetricValue(
  value: number | null,
  sampleSize: number,
  source: string,
  directionality: Directionality,
  status: DataSourceStatus = 'live',
  trend: number | null = null,
): MetricValue {
  return {
    value,
    trend,
    status,
    source,
    sample_size: sampleSize,
    sufficiency: classifySufficiency(sampleSize),
    directionality,
  };
}

/** Create a NOT_CONNECTED MetricValue */
export function toNotConnectedMetric(
  integration: keyof typeof NOT_CONNECTED_REGISTRY,
  directionality: Directionality = 'neutral',
): MetricValue {
  return {
    value: null,
    trend: null,
    status: 'not_connected',
    source: NOT_CONNECTED_REGISTRY[integration].label,
    sample_size: 0,
    sufficiency: 'empty',
    directionality,
  };
}

/** Map view rows to ScoreBandBucket[] */
export function buildScoreBands(rows: FinancialHealthRow[]): ScoreBandBucket[] {
  return rows.map((r) => ({
    band: r.score_band,
    member_count: r.member_count ?? 0,
    avg_score: r.avg_score ?? 0,
    avg_dti: r.avg_dti,
    avg_reserves: r.avg_reserves,
    avg_cash_flow_stability: r.avg_cash_flow_stability,
  }));
}

// ============================================================================
// API response shape (from GET /lender/insights)
// ============================================================================
/** P2B+P3+P4C: Behavioral aggregate from survey + segment + CoreMath data */
export interface BehavioralSummary {
  high_financial_stress_count: number;
  dti_high_count: number;
  /** P4C: Members with VeraScore decline ≥ 15 points in 90 days */
  declining_score_90d_count: number;
  /** P4C-2B: Members with < 1 month liquid reserves */
  liquidity_months_critical_count: number;
}

export interface InsightsApiResponse {
  tenant_id: string;
  org_id: string;
  financial_health: FinancialHealthRow[];
  overview: OverviewRow[];
  acquisition: AcquisitionRow[];
  underserved: Array<Omit<FinancialHealthRow, 'avg_cash_flow_stability' | 'monthly_avg_score'> & { avg_cash_flow_stability?: null; monthly_avg_score?: null }>;
  loan_performance: never[];
  behavioral?: BehavioralSummary;
  meta: {
    fetched_at: string;
    timestamp: string;
    counts: Record<string, number>;
  };
}

// ============================================================================
// Single API call — replaces 5 direct Supabase queries
// ============================================================================

/**
 * Fetch all insights data from the server-side API endpoint.
 * The API handles tenant_id resolution and uses service_role (bypasses RLS).
 */
export async function fetchAllInsights(): Promise<{
  data: InsightsApiResponse | null;
  error: Error | null;
}> {
  try {
    if (import.meta.env.DEV) console.log('📊 [insightsService] Fetching insights via API...');

    // P4C-1.1: Forward simulation query params to API (DEV only, does not persist to DB)
    let insightsUrl = `${API_BASE}/verascore/lender/insights`;
    if (import.meta.env.DEV) {
      const pageParams = new URLSearchParams(window.location.search);
      const simParam = pageParams.get('simulate_declining_score_90d');
      if (simParam === 'true') {
        const simCountParam = pageParams.get('simulate_declining_score_90d_count');
        const apiParams = new URLSearchParams({ simulate_declining_score_90d: 'true' });
        if (simCountParam) apiParams.set('simulate_declining_score_90d_count', simCountParam);
        insightsUrl += `?${apiParams.toString()}`;
        console.warn('[P4C] Simulation mode active: declining_score_90d_count will be overridden');
      }
    }

    const response = await fetch(insightsUrl, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: 'lender_review',
      },
      credentials: 'include',
    });

    // Handle auth errors — same pattern as lenderApiService.ts
    if (response.status === 401) {
      if (import.meta.env.DEV) console.warn('⚠️ [insightsService] Session expired (401), redirecting to logout...');
      window.history.replaceState({}, '', '?view=logout');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return { data: null, error: new Error('Session expired') };
    }
    if (response.status === 403) {
      if (import.meta.env.DEV) console.warn('🚫 [insightsService] Permission denied (403)');
      return { data: null, error: new Error('Permission denied') };
    }

    if (!response.ok) {
      const errorText = await response.text();
      if (import.meta.env.DEV) console.error('❌ [insightsService] API error:', response.status, errorText);
      return { data: null, error: new Error(`API error ${response.status}: ${errorText}`) };
    }

    const json = await response.json();
    const result: InsightsApiResponse = json?.data || json;

    // Normalize underserved rows to match FinancialHealthRow shape
    if (result.underserved) {
      result.underserved = result.underserved.map((d: any) => ({
        ...d,
        avg_cash_flow_stability: d.avg_cash_flow_stability ?? null,
        monthly_avg_score: d.monthly_avg_score ?? null,
      }));
    }

    if (import.meta.env.DEV) {
      console.log(
        `✅ [insightsService] Insights fetched: tenant_id=${result.tenant_id}, ` +
        `FH=${result.financial_health?.length} OV=${result.overview?.length} ` +
        `ACQ=${result.acquisition?.length} US=${result.underserved?.length}`,
      );
    }

    return { data: result, error: null };
  } catch (error) {
    if (import.meta.env.DEV) console.error('❌ [insightsService] Unexpected error:', error);
    return { data: null, error: error as Error };
  }
}
